/* istanbul ignore file */

import { gridNameList } from '@ad-lucy/components/layout/Grid/constants';
import { Arrows } from '@ad-lucy/components/nav/NavigationArrow/types';
import { defaultBreakpoints } from '@ad-lucy/context/ThemeProvider/constants';
import { defaultTheme } from '@ad-lucy/styled';
import type { objectType } from '@ad-lucy/utils';
import { ArrayToObject } from '@ad-lucy/utils';
import { lazy } from 'react';
import {
  ANIMATION_ITERATION_TYPE,
  BACKGROUND_TRANSITION_TYPE,
  countdownUnitVisibilityConfig,
  DEFAULT_MOBILE_INFO_BUTTON_ICON,
  enumConfigs,
  HOVER_TARGET,
  IMAGE_ANIMATION_TYPE,
  IMAGE_COMPARATOR_DESIGN,
  IMAGE_THEME,
  LAYER_BUTTON_POSITION,
  META_TYPE,
  metaConfigs,
  POSITION,
  SLIDE_DIRECTION,
  STICKY_CTA_ANIMATION_TYPE,
  STORE_FINDER_TEMPLATE,
  SWIPER_DIRECTION,
  TRANSITION_EFFECT,
  VIDEO_CONTROLS_VISIBILITY,
} from './constants';
import type { CreativeId, FullCreativesConfig } from './types';

const defaultCreativesConfig = {
  id: null as unknown as CreativeId,
  name: 'Default Ad',
  hasClickTrackerPreventation: false,
  settings: {
    continueWatchingOverlay: false,
    commonMuteState: false,
    // Indicates whether autoplay is enabled for ad
    autoplay: true,
    disableAutoplayOnInitialInteraction: false,
    playVideoIfNotInView: true,
    // Indicates whether we should use the duration_activated, if set to true, duration_activated is not considered
    // TODO: should be removed when this task is completed: https://factor-eleven.atlassian.net/browse/AD-1873
    forceAutoplay: false,
    // Indicates whether ad should continue playing if not in active tab.
    // Note: video only continues if the browser allowes it. It can happen that the browser puts the tab on pause to save memory and processing power. Ad will continue playing only if the ad is not completed.
    playIfNotInActiveTab: false,
    // TODO: Use headerPadding and footerPadding in other components (e.g. get rid of headerFooterPadding in Text component)
    // Indicates if elements which should not overflow the header should have a padding to top.
    headerPadding: {},
    // Indicates if elements which should not overflow the footer should have a padding to bottom.
    footerPadding: {},
    hideFooter: false,
    headerHeight: {},
    headerEnableHeightSettingsDifferentViewports: true,
    startupAnimation: true,
    // Set to true if video on demand should be enabled regardless of the setting in the video component or meta data
    forceVideoOnDemand: false,
  },
  breakpoints: defaultBreakpoints,
  navigation: {
    defaults: {
      showOnHover: false,
      direction: SWIPER_DIRECTION.HORIZONTAL,
      loop: null,
      pauseOnMouseEnter: true,
      enableOverlay: true,
      virtual: undefined,
    },
    arrows: Arrows.COLORED,
    transition: TRANSITION_EFFECT.SLIDE,
    swiperBreakpointOptions: {},
    trackInitialViewOnlyIfInActiveTab: false,
  },
  grid: {
    grids: gridNameList,
    keepAspectRatio: false,
  },
  ambilight: {
    defaults: {
      active: false,
      blur: 50,
      fallbackColor: 'rgba(255, 255, 255, 0)',
      opacity: 0.75,
    },
  },
  googleHeavyAd: {
    defaults: {
      active: true,
    },
  },
  layer: {
    defaults: {
      closeButton: false,
      closeButtonPosition: LAYER_BUTTON_POSITION.TOP_RIGHT,
      closeButtonBackgroundColor: '#000000',
      closeButtonTextColor: '#ffffff',
      mobileLayer: true,
      mobileInfoButtonIcon: DEFAULT_MOBILE_INFO_BUTTON_ICON,
    },
  },
  components: {
    callToAction: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/CallToAction')),
      defaults: {
        color: '#FFFFFF',
        colorHover: '#000000',
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        text: {
          type: META_TYPE.STRING,
        },
        linkOut: {
          type: META_TYPE.STRING,
        },
        color: {
          type: META_TYPE.COLOR_PICKER,
        },
        colorHover: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    button: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/CTAButton')),
      defaults: {
        color: '#FFFFFF',
        colorHover: '#000000',
        text: '',
      },
      settings: {},
      meta: {
        text: {
          type: META_TYPE.STRING,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        textColorHover: {
          type: META_TYPE.COLOR_PICKER,
        },
        linkOut: {
          type: META_TYPE.STRING,
        },
        color: {
          type: META_TYPE.COLOR_PICKER,
        },
        colorHover: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    category: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/CategoryMenu')),
      defaults: {
        sorting: 0,
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        text: {
          type: META_TYPE.STRING,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_hover: {
          type: META_TYPE.IMAGE,
        },
        logo: {
          type: META_TYPE.ENUM,
          oneOf: ['1', '2'],
        },
      },
    },
    imageComparator: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ImageComparator')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        ...metaConfigs.navigation,
        design: {
          type: META_TYPE.ENUM,
          oneOf: [IMAGE_COMPARATOR_DESIGN.ROUND, IMAGE_COMPARATOR_DESIGN.ELONGATED],
        },
        dividingLineColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        dividingLineWidth: {
          type: META_TYPE.INTEGER,
        },
        imageLeft_l: {
          type: META_TYPE.IMAGE,
        },
        imageLeft_m: {
          type: META_TYPE.IMAGE,
        },
        imageLeft_s: {
          type: META_TYPE.IMAGE,
        },
        imageRight_l: {
          type: META_TYPE.IMAGE,
        },
        imageRight_m: {
          type: META_TYPE.IMAGE,
        },
        imageRight_s: {
          type: META_TYPE.IMAGE,
        },
        link: {
          type: META_TYPE.STRING,
        },
        sliderElementBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        sliderElementColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        sliderElementWidth: {
          type: META_TYPE.INTEGER,
        },
        sliderInitialVisible: {
          type: META_TYPE.BOOLEAN,
        },
        imageLeft_dynamicScaling: {
          type: META_TYPE.BOOLEAN,
        },
        imageRight_dynamicScaling: {
          type: META_TYPE.BOOLEAN,
        },
        imageLeft_l_focus: enumConfigs.focus,
        imageLeft_m_focus: enumConfigs.focus,
        imageLeft_s_focus: enumConfigs.focus,
        imageRight_l_focus: enumConfigs.focus,
        imageRight_m_focus: enumConfigs.focus,
        imageRight_s_focus: enumConfigs.focus,
      },
    },
    closeAd: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/CloseAd')),
      defaults: {
        centered: false,
        sorting: 0,
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        text: {
          type: META_TYPE.STRING,
        },
        submitPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    copyright: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Copyright')),
      defaultMetaData: {
        image: IMAGE_THEME.LIGHT,
      },
      defaults: {},
      settings: {},
      meta: {
        image: {
          type: META_TYPE.ENUM,
          oneOf: [IMAGE_THEME.DARK, IMAGE_THEME.LIGHT],
          required: true,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    countdown: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Countdown')),
      defaults: {
        backgroundColor: '#ffffff',
        borderColor: '#ffffff',
        borderRadius: 8,
        color: '#000000',
        compact: false,
        compactText: '',
        template: 'basic',
        text: '',
        alignment: { top: 0, left: 0 },
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderRadius: {
          type: META_TYPE.INTEGER,
        },
        color: {
          type: META_TYPE.COLOR_PICKER,
        },
        compact: {
          type: META_TYPE.BOOLEAN,
        },
        compactText: {
          type: META_TYPE.TEXT,
        },
        template: {
          type: META_TYPE.ENUM,
          oneOf: [
            STORE_FINDER_TEMPLATE.BASIC,
            STORE_FINDER_TEMPLATE.CALENDAR,
            STORE_FINDER_TEMPLATE.EUROPE,
          ],
        },
        text: {
          type: META_TYPE.TEXT,
        },
        finishText: {
          type: META_TYPE.TEXT,
        },
        date: {
          type: META_TYPE.DATETIME,
        },
        verticalPosition: {
          type: META_TYPE.STICKY_POSITION,
        },
        enableDisplaySettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        displaySettings: {
          type: META_TYPE.OBJECT,
          structure: {
            mobile: countdownUnitVisibilityConfig,
            hpa: countdownUnitVisibilityConfig,
            desktop: countdownUnitVisibilityConfig,
          },
        },
      },
    },
    custom: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Custom')),
      defaults: {},
      settings: {},
      meta: {
        data: {
          type: META_TYPE.JSON,
          structure: {
            clickPayout: { type: META_TYPE.BOOLEAN },
          },
        },
        category: { type: META_TYPE.STRING },
        key: {
          type: META_TYPE.STRING,
          required: true,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    footerLogo: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/FooterLogo')),
      defaults: {
        hoverText: '',
        sorting: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        image_l: {
          type: META_TYPE.IMAGE,
          required: true,
        },
        individualLogo: {
          type: META_TYPE.BOOLEAN,
        },
        logo: {
          type: META_TYPE.STRING,
        },
      },
    },
    headerLogo: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/HeaderLogo')),
      defaults: {
        hoverText: '',
        sorting: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        image_l: {
          type: META_TYPE.IMAGE,
          required: true,
        },
        individualLogo: {
          type: META_TYPE.BOOLEAN,
        },
        logo: {
          type: META_TYPE.STRING,
        },
        distanceTop: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        distanceBottom: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        enableDistanceSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    html: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/HTML')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        code: {
          type: META_TYPE.HTML,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    iframe: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Iframe')),
      defaults: {},
      settings: {},
      meta: {
        src: {
          type: META_TYPE.STRING,
        },
        title: {
          type: META_TYPE.STRING,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    image: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Image')),
      defaults: {
        advertisementLabel: false,
        ambilightActive: false,
        ambilightBlur: 50,
        ambilightFallbackColor: 'rgba(255, 255, 255, 0)',
        ambilightOpacity: 0.75,
        avatar: '',
        caption: '',
        fullSize: false,
        title: '',
        size: {
          width: 100,
          height: 100,
        },
        margin: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        mobileInfoButtonIcon: DEFAULT_MOBILE_INFO_BUTTON_ICON,
      },
      defaultMetaData: {
        async: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        ambilight_active: {
          type: META_TYPE.BOOLEAN,
        },
        ambilight_blur: {
          type: META_TYPE.RANGE,
        },
        ambilight_fallback_color: {
          type: META_TYPE.COLOR_PICKER,
        },
        ambilight_opacity: {
          type: META_TYPE.RANGE,
        },
        fullsize: {
          type: META_TYPE.BOOLEAN,
        },
        l: {
          type: META_TYPE.IMAGE,
        },
        m: {
          type: META_TYPE.IMAGE,
        },
        s: {
          type: META_TYPE.IMAGE,
        },
        billboard: {
          type: META_TYPE.IMAGE,
        },
        product_link: {
          type: META_TYPE.STRING,
        },
        hoverEffect: {
          type: META_TYPE.OBJECT,
          structure: {
            zoomLevel: { type: META_TYPE.INTEGER },
            slideStartPosition: {
              type: META_TYPE.ENUM,
              oneOf: [POSITION.LEFT, POSITION.RIGHT, POSITION.TOP, POSITION.BOTTOM],
            },
            slideDirection: {
              type: META_TYPE.ENUM,
              oneOf: [SLIDE_DIRECTION.IN, SLIDE_DIRECTION.OUT],
            },
            mobileLayer: {
              type: META_TYPE.OBJECT,
              structure: {
                enabled: { type: META_TYPE.BOOLEAN },
                position: enumConfigs.cssPosition,
                color: { type: META_TYPE.COLOR_PICKER },
                textColor: { type: META_TYPE.COLOR_PICKER },
                applyGlobalSettings: { type: META_TYPE.BOOLEAN },
              },
            },
          },
        },
        dynamicScaling: {
          type: META_TYPE.BOOLEAN,
        },
        hoverEffect_enabled: {
          type: META_TYPE.BOOLEAN,
        },
        async: {
          type: META_TYPE.BOOLEAN,
        },
        size: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        enableSizeSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        size_s: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        size_m: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        size_b: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        margin: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        margin_s: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        margin_m: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        margin_b: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        billboard_focus: enumConfigs.focus,
        l_focus: enumConfigs.focus,
        m_focus: enumConfigs.focus,
        s_focus: enumConfigs.focus,
        mobileInfoButtonIcon: enumConfigs.mobileInfoButtonIcon,
      },
    },
    imageAndText: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ImageAndText')),
      defaults: {
        layout: 'imageLeft',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        externalLink: {
          type: 'url',
        },
        image_l: {
          type: META_TYPE.IMAGE,
          required: true,
        },
        layout: {
          type: META_TYPE.STRING,
        },
        text: {
          type: META_TYPE.TEXT,
        },
      },
    },
    imageMap: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ImageMap')),
      defaults: {
        cursorType: 'cursor-zoomin',
        enableZoom: true,
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        cursor: {
          type: META_TYPE.ENUM,
          oneOf: ['cursor-zoomin', 'cursor-pointer'],
          required: true,
        },
        image: {
          type: 'image_map',
          required: true,
        },
        link: {
          type: 'url',
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        zoom: {
          type: META_TYPE.BOOLEAN,
        },
        zoomPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    imageMapArea: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ImageMapArea')),
      defaults: {
        name: '',
        title: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        coords: {
          type: 'image_map_coords',
          required: true,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        title: {
          type: META_TYPE.STRING,
        },
      },
    },
    spotlights: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Spotlights')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        imageL: {
          type: META_TYPE.IMAGE,
        },
        imageM: {
          type: META_TYPE.IMAGE,
        },
        imageS: {
          type: META_TYPE.IMAGE,
        },
        imageB: {
          type: 'image',
        },
        productRelation: {
          type: META_TYPE.STRING,
        },
      },
    },
    spotlightsArea: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SpotlightsArea')),
      defaults: {
        name: '',
        windowBackground: 'rgba(255, 255, 255, 0.8)',
        windowBorderRadius: 8,
        windowWidth: 240,
        windowHeight: 320,
        pulseAnimationDuration: 1000,
      },
      settings: {},
      meta: {
        l: {
          type: META_TYPE.OBJECT,
          structure: {
            windowWidth: { type: META_TYPE.INTEGER },
            windowHeight: { type: META_TYPE.INTEGER },
            coords: { type: META_TYPE.STRING },
          },
        },
        m: {
          type: META_TYPE.OBJECT,
          structure: {
            windowWidth: { type: META_TYPE.INTEGER },
            windowHeight: { type: META_TYPE.INTEGER },
            coords: { type: META_TYPE.STRING },
          },
        },
        s: {
          type: META_TYPE.OBJECT,
          structure: {
            windowWidth: { type: META_TYPE.INTEGER },
            windowHeight: { type: META_TYPE.INTEGER },
            coords: { type: META_TYPE.STRING },
          },
        },
        b: {
          type: 'object',
          structure: {
            windowWidth: { type: 'integer' },
            windowHeight: { type: 'integer' },
            coords: { type: 'string' },
          },
        },
        billboardMarkerEnabled: {
          type: 'boolean',
        },
        pointInnerColor: {
          type: META_TYPE.STRING,
        },
        pointInnerSize: {
          type: META_TYPE.STRING,
        },
        pointOuterColor: {
          type: META_TYPE.STRING,
        },
        pointOuterSize: {
          type: META_TYPE.STRING,
        },
        popupAnimation: {
          type: META_TYPE.OBJECT,
          structure: {
            type: {
              type: META_TYPE.ENUM,
              oneOf: [
                IMAGE_ANIMATION_TYPE.ZOOM_IN,
                IMAGE_ANIMATION_TYPE.ZOOM_OUT,
                IMAGE_ANIMATION_TYPE.SLIDE,
                IMAGE_ANIMATION_TYPE.FADE_IN,
              ],
            },
            duration: { type: META_TYPE.INTEGER },
          },
        },
        pulseAnimation: {
          type: META_TYPE.BOOLEAN,
        },
        windowBackground: {
          type: META_TYPE.STRING,
        },
        windowBorderRadius: {
          type: 'float',
        },
      },
    },
    infoModal: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/InfoModal')),
      defaults: {
        mouseOverActivation: false,
        position: 'bottomCenter',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        linkText: {
          type: META_TYPE.TEXT,
          required: true,
        },
        linkTitle: {
          type: META_TYPE.TEXT,
        },
        mouseoverActivation: {
          type: META_TYPE.BOOLEAN,
        },
        position: {
          type: META_TYPE.ENUM,
          oneOf: [
            LAYER_BUTTON_POSITION.BOTTOM_LEFT,
            LAYER_BUTTON_POSITION.BOTTOM_CENTER,
            LAYER_BUTTON_POSITION.BOTTOM_RIGHT,
          ],
        },
        text: {
          type: META_TYPE.TEXT,
        },
      },
    },
    link: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/HeaderLink')),
      defaults: {
        sorting: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_hover: {
          type: META_TYPE.IMAGE,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    mediaControls: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/MediaControls')),
      defaults: {
        visibleControls: 'all',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        color: {
          type: META_TYPE.COLOR_PICKER,
        },
        visibleControls: {
          type: META_TYPE.ENUM,
          oneOf: [
            VIDEO_CONTROLS_VISIBILITY.ALL,
            VIDEO_CONTROLS_VISIBILITY.MUTE_ONLY,
            VIDEO_CONTROLS_VISIBILITY.PLAY_PAUSE_ONLY,
          ],
        },
      },
    },
    modal: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/FooterModal')),
      defaults: {
        mouseOverActivation: false,
        closeOnMouseLeave: false,
      },
      settings: {
        maxWidth: 'calc(100vh * (2 / 3) - 130px)',
      },
      meta: {
        ...metaConfigs.payouts,
        closeOnMouseLeave: {
          type: META_TYPE.BOOLEAN,
        },
        label_header: {
          type: META_TYPE.TEXT,
        },
        label_paragraph: {
          type: META_TYPE.TEXT,
        },
        label_text: {
          type: META_TYPE.STRING,
        },
        mouseOverActivation: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    playlist: {
      wrapper: null,
      defaults: {},
      settings: {},
      meta: {},
    },
    pricePatch: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/PricePatch')),
      defaults: {
        autoScale: false,
        position: { left: 0, top: 0 },
        scale: 100,
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        ...metaConfigs.navigation,
        autoScale: {
          type: META_TYPE.BOOLEAN,
        },
        basicPriceIndication: {
          type: META_TYPE.STRING,
        },
        payout: {
          type: META_TYPE.BOOLEAN,
        },
        position: {
          type: META_TYPE.OBJECT,
          structure: {
            useDefault: { type: META_TYPE.BOOLEAN },
            l: {
              type: META_TYPE.OBJECT,
              structure: { top: { type: META_TYPE.STRING }, left: { type: META_TYPE.STRING } },
            },
            m: {
              type: META_TYPE.OBJECT,
              structure: { top: { type: META_TYPE.STRING }, left: { type: META_TYPE.STRING } },
            },
            s: {
              type: META_TYPE.OBJECT,
              structure: { top: { type: META_TYPE.STRING }, left: { type: META_TYPE.STRING } },
            },
          },
        },
        postPrice: {
          type: META_TYPE.STRING,
        },
        postPriceOld: {
          type: META_TYPE.STRING,
        },
        postSaving: {
          type: META_TYPE.STRING,
        },
        prePrice: {
          type: META_TYPE.STRING,
        },
        prePriceOld: {
          type: META_TYPE.STRING,
        },
        preSaving: {
          type: META_TYPE.STRING,
        },
        price: {
          type: META_TYPE.STRING,
          required: true,
        },
        priceOld: {
          type: META_TYPE.STRING,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        saving: {
          type: META_TYPE.STRING,
        },
        scale: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        template: {
          type: META_TYPE.STRING,
        },
        font: {
          type: META_TYPE.STRING,
        },
        zoomOnHover: {
          type: META_TYPE.BOOLEAN,
        },
        enablePositionSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        enableScaleSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    programmaticHpa: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ProgrammaticHPA')),
      defaults: {},
      settings: {},
      meta: {
        dealAdUnit: {
          type: META_TYPE.STRING,
          required: true,
        },
      },
    },
    slider: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Slider')),
      defaults: {},
      settings: {
        autoplayOnlyInView: false,
      },
      meta: {
        arrows: {
          type: META_TYPE.BOOLEAN,
        },
        arrowsAlwaysVisible: {
          type: META_TYPE.BOOLEAN,
        },
        autoplay: {
          type: META_TYPE.STRING,
        },
        dots: {
          type: META_TYPE.BOOLEAN,
        },
        fade: {
          type: META_TYPE.BOOLEAN,
        },
        infinite: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        slidesToScroll: {
          type: META_TYPE.INTEGER,
        },
        slidesToShow: {
          type: META_TYPE.INTEGER,
        },
        speed: {
          type: META_TYPE.STRING,
        },
      },
      defaultMetaData: {},
    },
    sliderContainer: {
      wrapper: null,
      defaults: {},
      settings: {},
      meta: {},
    },
    search: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/HeaderSearch')),
      defaults: {
        sorting: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        clickChildPayout: {
          type: META_TYPE.BOOLEAN,
        },
        hoverChildPayout: {
          type: META_TYPE.BOOLEAN,
        },
        inputPayout: {
          type: META_TYPE.BOOLEAN,
        },
        noResult: {
          type: META_TYPE.BOOLEAN,
        },
        noResultToShopClick: {
          type: META_TYPE.BOOLEAN,
        },
        noResultToShopClick_link: {
          type: META_TYPE.STRING,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_hover: {
          type: META_TYPE.IMAGE,
        },
        noResultPayout: {
          type: META_TYPE.BOOLEAN,
        },
        noResultToShopClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    socialFlyoutBar: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SocialFlyoutBar')),
      defaults: {},
      settings: {},
      meta: {
        clickPayout: {
          type: META_TYPE.BOOLEAN,
          required: true,
        },
        hoverPayout: {
          type: META_TYPE.BOOLEAN,
          required: true,
        },
        infoText: {
          type: META_TYPE.TEXT,
        },
        showOriginalIcons: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    socialMediaBar: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SocialMediaBar')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        infoText: {
          type: META_TYPE.TEXT,
        },
        showOriginalIcons: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    socialmedia: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SocialMedia')),
      defaults: {
        category: '',
        icon: '',
        showOriginalIcons: false,
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        ...metaConfigs.navigation,
        category: {
          type: META_TYPE.STRING,
          required: true,
        },
        icon: {
          type: META_TYPE.IMAGE,
        },
        infoText: {
          type: META_TYPE.TEXT,
        },
        link: {
          type: META_TYPE.STRING,
          required: true,
        },
        showOriginalIcons: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    socialmediaFooter: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SocialMediaFooter')),
      defaults: {
        category: '',
        icon: '',
        showOriginalIcons: false,
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        category: {
          type: META_TYPE.STRING,
          required: true,
        },
        enableLocalPayouts: {
          type: META_TYPE.BOOLEAN,
          required: true,
        },
        icon: {
          type: META_TYPE.IMAGE,
        },
        link: {
          type: META_TYPE.STRING,
          required: true,
        },
        showOriginalIcons: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    stickyCTA: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StickyCTA')),
      defaults: {
        align: { left: 0, top: 0 },
        autoScale: true,
        borderRadius: { l: 0, m: 0, s: 0, b: 0 },
        paddingHorizontal: { l: 10, m: 10, s: 10, b: 10 },
        paddingVertical: { l: 5, m: 5, s: 5, b: 5 },
        borderWidth: { l: 2, m: 2, s: 2, b: 2 },
        position: { left: 0, top: 0 },
        pulseAnimation: false,
        isAnimated: false,
        hoverTarget: HOVER_TARGET.COMPONENT,
        hoverOverlayBackgroundColor: 'rgba(0, 0, 0, 0.35)',
        showHoverOverlay: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        align: {
          type: 'sticky_align',
          required: true,
        },
        anchor: {
          type: META_TYPE.INTEGER,
        },
        animationIteration: {
          type: META_TYPE.ENUM,
          oneOf: [ANIMATION_ITERATION_TYPE.ON_HOVER, ANIMATION_ITERATION_TYPE.ALWAYS],
        },
        animationType: {
          type: META_TYPE.ENUM,
          oneOf: [
            STICKY_CTA_ANIMATION_TYPE.PULSE,
            STICKY_CTA_ANIMATION_TYPE.PULSE_GROW,
            STICKY_CTA_ANIMATION_TYPE.BOUNCE_IN,
            STICKY_CTA_ANIMATION_TYPE.WOBBLE_HORIZONTAL,
            STICKY_CTA_ANIMATION_TYPE.WOBBLE_VERTICAL,
            STICKY_CTA_ANIMATION_TYPE.BUZZ,
          ],
        },
        autoAlign: {
          type: META_TYPE.BOOLEAN,
        },
        autoScale: {
          type: META_TYPE.BOOLEAN,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        backgroundTransition: {
          type: META_TYPE.ENUM,
          oneOf: [
            BACKGROUND_TRANSITION_TYPE.FADE,
            BACKGROUND_TRANSITION_TYPE.SWEEP_TO_RIGHT,
            BACKGROUND_TRANSITION_TYPE.RECTANGLE_OUT,
            BACKGROUND_TRANSITION_TYPE.SHUTTER_OUT_VERTICAL,
          ],
        },
        borderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderRadius: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        borderWidth: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        hoverBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        hoverBorderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        hoverOverlayBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        hoverTarget: {
          type: META_TYPE.ENUM,
          oneOf: [HOVER_TARGET.TILE, HOVER_TARGET.COMPONENT],
        },
        hoverTextColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        isAnimated: {
          type: META_TYPE.BOOLEAN,
        },
        link: {
          type: 'url',
        },
        paddingHorizontal: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        paddingVertical: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        position: {
          type: META_TYPE.STICKY_POSITION,
          required: true,
        },
        position_m: {
          type: META_TYPE.STICKY_POSITION,
        },
        position_s: {
          type: META_TYPE.STICKY_POSITION,
        },
        pulseAnimation: {
          type: META_TYPE.BOOLEAN,
        },
        showHoverOverlay: {
          type: META_TYPE.BOOLEAN,
        },
        showOnlyOnHover: {
          type: META_TYPE.BOOLEAN,
        },
        text: {
          type: META_TYPE.STRING,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    stickyImage: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StickyImage')),
      defaults: {
        borderRadius: 0,
        hoverAnimation: false,
        paddingHorizontal: 10,
        paddingVertical: 5,
        position: { left: 0, top: 0 },
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        align: {
          type: 'sticky_align',
          required: true,
        },
        anchor: {
          type: META_TYPE.INTEGER,
        },
        autoAlign: {
          type: META_TYPE.BOOLEAN,
        },
        autoScale: {
          type: META_TYPE.BOOLEAN,
        },
        HoverAnimation: {
          type: META_TYPE.BOOLEAN,
        },
        image_l: {
          type: META_TYPE.IMAGE,
          required: true,
        },
        image_m: {
          type: META_TYPE.IMAGE,
        },
        image_s: {
          type: META_TYPE.IMAGE,
        },
        image_b: {
          type: META_TYPE.IMAGE,
        },
        position: {
          type: META_TYPE.STICKY_POSITION,
          required: true,
        },
        position_m: {
          type: META_TYPE.STICKY_POSITION,
        },
        position_s: {
          type: META_TYPE.STICKY_POSITION,
        },
        position_b: {
          type: META_TYPE.STICKY_POSITION,
        },
        enablePositionSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    stickyText: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StickyText')),
      defaults: {
        autoScale: true,
        align: { left: '0%', top: '0%' },
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderRadius: 0,
        padding: 0,
        position: { left: '5%', top: '5%' },
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        align: {
          type: 'sticky_align',
          required: true,
        },
        autoAlign: {
          type: META_TYPE.BOOLEAN,
        },
        autoScale: {
          type: META_TYPE.BOOLEAN,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        body: {
          type: META_TYPE.TEXT,
          required: true,
        },
        borderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderRadius: {
          type: META_TYPE.INTEGER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        linkOut: {
          type: 'url',
        },
        padding: {
          type: META_TYPE.INTEGER,
        },
        position: {
          type: META_TYPE.STICKY_POSITION,
          required: true,
        },
        position_m: {
          type: META_TYPE.STICKY_POSITION,
        },
        position_s: {
          type: META_TYPE.STICKY_POSITION,
        },
        position_b: {
          type: META_TYPE.STICKY_POSITION,
        },
        enablePositionSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    textButton: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/TextButton')),
      defaults: {
        position: 'bottomCenter',
        textColor: '#fff',
        textBackgroundColor: '#000',
        textBorderColor: '#ff0000',
        hoverColor: '#0000ff',
        hoverBackgroundColor: '#6a5acd',
        hoverBorderColor: '#3cb371',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        animationPulse: {
          type: META_TYPE.BOOLEAN,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        backgroundHoverColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderHoverColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        description: {
          type: META_TYPE.TEXT,
        },
        position: {
          type: META_TYPE.ENUM,
          oneOf: [
            LAYER_BUTTON_POSITION.BOTTOM_LEFT,
            LAYER_BUTTON_POSITION.BOTTOM_CENTER,
            LAYER_BUTTON_POSITION.BOTTOM_RIGHT,
          ],
        },
        text: {
          type: META_TYPE.STRING,
          required: true,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        textHoverColor: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    store: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Store')),
      defaults: {
        branchLogo: '',
        branchNumber: 1,
        branchRadius: 100,
        changeStoreLabel: 'Markt ändern',
        countryCode: 'de',
        enableToMarket: false,
        enableRoutePlaner: false,
        enableStoreChange: false,
        enableIndividualLogo: false,
        labelText: 'Zum Markt',
        mainContent:
          '<p><strong>Adresse</strong></p><p>Kaufland Wetzlar</p><p>Am Forum 1</p><p>35576 Wetzlar</p><p><br></p><p><strong>Kontakt</strong></p><p>E-Mail: <a href="mailto:"></a></p><p>Telefon: <a href="tel:"></a></p>',
        searchTitle: 'Ihre Filialauswahl:',
        sideContent:
          '<p><strong>Öffnungszeiten</strong></p><p><span class="branch-open-day branch-open-day--monday"><span class="branch-open-day-label">Montag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--tuesday"><span class="branch-open-day-label">Dienstag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--wednesday"><span class="branch-open-day-label">Mittwoch:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--thursday"><span class="branch-open-day-label">Donnerstag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--friday"><span class="branch-open-day-label">Freitag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--saturday"><span class="branch-open-day-label">Samstag:</span> 07:00-22:00 Uhr<br/></span></p>',
        zoomLevel: 13,
        linkOutOnStoreClick: true,
        defaultAndDetailsClickTrackerTrigger: 'content-branch',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        branchNumber: {
          type: META_TYPE.INTEGER,
          required: true,
        },
        branchRadius: {
          type: META_TYPE.INTEGER,
          required: true,
        },
        changeStoreLabel: {
          type: META_TYPE.STRING,
        },
        columnDisplayOptions: {
          type: META_TYPE.ENUM,
          oneOf: ['branch-image', 'branch-logo'],
        },
        enableIndividualLogo: {
          type: META_TYPE.BOOLEAN,
        },
        enableRoutePlaner: {
          type: META_TYPE.BOOLEAN,
        },
        enableStoreChange: {
          type: META_TYPE.BOOLEAN,
        },
        enableToMarket: {
          type: META_TYPE.BOOLEAN,
        },
        headerLabel: {
          type: META_TYPE.STRING,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_hover: {
          type: META_TYPE.IMAGE,
        },
        labelText: {
          type: META_TYPE.STRING,
        },
        mainContent: {
          type: META_TYPE.TEXT,
        },
        mapMarker: {
          type: META_TYPE.IMAGE,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        routePlannerClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        searchTitle: {
          type: META_TYPE.STRING,
        },
        sideContent: {
          type: META_TYPE.TEXT,
        },
        storeChangeClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        text: {
          type: META_TYPE.STRING,
        },
        toMarketClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        zoomLevel: {
          type: META_TYPE.INTEGER,
          required: true,
        },
      },
    },
    enhancedStore: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/EnhancedStore')),
      defaults: {
        branchNumber: 1,
        branchRadius: 100,
        backgroundColor: '#f7f7f7',
        countryCode: 'de',
        enableRoutePlaner: false,
        enableStoreChange: true,
        showStoreChangeModalOnPageActive: false,
        zoomLevel: 13,
        linkOutOnStoreClick: true,
        openingHours: {
          enabled: false,
          weekdays: {
            monday: { label: '', value: '' },
            tuesday: { label: '', value: '' },
            wednesday: { label: '', value: '' },
            thursday: { label: '', value: '' },
            friday: { label: '', value: '' },
            saturday: { label: '', value: '' },
            sunday: { label: '', value: '' },
          },
        },
      },
      settings: {},
      meta: {
        address: {
          type: META_TYPE.STRING,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        branchNumber: {
          type: 'float',
        },
        branchRadius: {
          type: 'float',
        },
        changeStoreLabel: {
          type: META_TYPE.STRING,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        defaultMapMarkerColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        enableRoutePlaner: {
          type: META_TYPE.BOOLEAN,
        },
        enableStoreChange: {
          type: META_TYPE.BOOLEAN,
        },
        headerLabel: {
          type: META_TYPE.STRING,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        mapMarker: {
          type: META_TYPE.IMAGE,
        },
        mapMarkerSize: {
          type: META_TYPE.INTEGER,
        },
        notification: {
          type: META_TYPE.STRING,
        },
        openingHours: {
          type: META_TYPE.OBJECT,
          structure: {
            enabled: { type: META_TYPE.BOOLEAN },
            weekdays: {
              type: META_TYPE.OBJECT,
              structure: {
                monday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                tuesday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                wednesday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                thursday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                friday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                saturday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
                sunday: {
                  type: META_TYPE.OBJECT,
                  structure: {
                    label: { type: META_TYPE.STRING },
                    value: { type: META_TYPE.STRING },
                  },
                },
              },
            },
          },
        },
        routePlannerClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        searchTitle: {
          type: META_TYPE.STRING,
        },
        services: {
          type: META_TYPE.OBJECT,
          structure: {
            label: { type: META_TYPE.STRING },
            tags: { type: 'array', items: { type: META_TYPE.STRING } },
          },
        },
        showStoreChangeModalOnPageActive: {
          type: META_TYPE.BOOLEAN,
        },
        storeChangeClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        zoomLevel: {
          type: 'float',
        },
        position: {
          type: META_TYPE.OBJECT,
          structure: {
            button: {
              type: META_TYPE.OBJECT,
              structure: {
                l: enumConfigs.cssPosition,
                m: enumConfigs.cssPosition,
                s: enumConfigs.cssPosition,
              },
            },
            openNow: {
              type: META_TYPE.OBJECT,
              structure: {
                l: enumConfigs.cssPosition,
                m: enumConfigs.cssPosition,
                s: enumConfigs.cssPosition,
              },
            },
            switcher: {
              type: META_TYPE.OBJECT,
              structure: {
                l: enumConfigs.cssPosition,
                m: enumConfigs.cssPosition,
                s: enumConfigs.cssPosition,
              },
            },
            routePlanner: {
              type: META_TYPE.OBJECT,
              structure: {
                l: enumConfigs.cssPosition,
                m: enumConfigs.cssPosition,
                s: enumConfigs.cssPosition,
              },
            },
          },
        },
        enableFontSizeSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        enablePositionSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        fontSize: {
          type: META_TYPE.OBJECT,
          structure: {
            storeDetailsButton: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            storeName: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            headline: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            addressHeader: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            openingHoursHeader: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            openingHours: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            openNow: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            servicesLabel: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            tags: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            changeMarketButton: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            notification: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            routePlanner: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
            marketSearch: {
              type: META_TYPE.OBJECT,
              structure: {
                l: { type: META_TYPE.INTEGER },
                m: { type: META_TYPE.INTEGER },
                s: { type: META_TYPE.INTEGER },
              },
            },
          },
        },
      },
    },
    storeV2: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StoreV2')),
      defaults: {
        branchLogo: '',
        branchNumber: 1,
        branchRadius: 100,
        changeStoreLabel: 'Markt ändern',
        countryCode: 'de',
        enableToMarket: false,
        enableRoutePlaner: false,
        enableStoreChange: false,
        enableIndividualLogo: false,
        labelText: 'Zum Markt',
        mainContent:
          '<p><strong>Adresse</strong></p><p>Kaufland Wetzlar</p><p>Am Forum 1</p><p>35576 Wetzlar</p><p><br></p><p><strong>Kontakt</strong></p><p>E-Mail: <a href="mailto:"></a></p><p>Telefon: <a href="tel:"></a></p>',
        searchTitle: 'Ihre Filialauswahl:',
        sideContent:
          '<p><strong>Öffnungszeiten</strong></p><p><span class="branch-open-day branch-open-day--monday"><span class="branch-open-day-label">Montag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--tuesday"><span class="branch-open-day-label">Dienstag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--wednesday"><span class="branch-open-day-label">Mittwoch:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--thursday"><span class="branch-open-day-label">Donnerstag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--friday"><span class="branch-open-day-label">Freitag:</span> 07:00-22:00 Uhr<br/></span><span class="branch-open-day branch-open-day--saturday"><span class="branch-open-day-label">Samstag:</span> 07:00-22:00 Uhr<br/></span></p>',
        zoomLevel: 13,
        linkOutOnStoreClick: true,
        headerLabel: 'Kaufland Wetzlar',
        color: '#000000',
        defaultAndDetailsClickTrackerTrigger: 'content-branch',
      },
      settings: {},
      meta: {
        routePlannerClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        storeChangeClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        branchNumber: {
          type: 'float',
        },
        branchRadius: {
          type: 'float',
        },
        changeStoreLabel: {
          type: META_TYPE.STRING,
        },
        columnDisplayOptions: {
          type: META_TYPE.ENUM,
          oneOf: ['branch-image', 'branch-logo'],
        },
        enableIndividualLogo: {
          type: META_TYPE.BOOLEAN,
        },
        enableRoutePlaner: {
          type: META_TYPE.BOOLEAN,
        },
        enableStoreChange: {
          type: META_TYPE.BOOLEAN,
        },
        enableToMarket: {
          type: META_TYPE.BOOLEAN,
        },
        labelText: {
          type: META_TYPE.STRING,
        },
        mainContent: {
          type: META_TYPE.STRING,
        },
        searchTitle: {
          type: META_TYPE.STRING,
        },
        sideContent: {
          type: META_TYPE.STRING,
        },
        headerLabel: {
          type: META_TYPE.STRING,
        },
        zoomLevel: {
          type: META_TYPE.INTEGER,
        },
        mapMarker: {
          type: META_TYPE.IMAGE,
        },
      },
    },
    storeFinder: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StoreFinder')),
      defaults: {
        backgroundColor: 'transparent',
        fieldsBackgroundColor: '#f7f7f7',
        inputPlaceholderColor: '#24242d',
        borderColor: 'transparent',
        textColor: '#24242d',
        titleTextColor: '#24242d',
        borderWidth: 1,
        countryCode: 'de',
      },
      settings: {},
      meta: {
        backgroundColor: {
          type: META_TYPE.STRING,
        },
        borderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        borderWidth: {
          type: META_TYPE.INTEGER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        fieldsBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        image_l: {
          type: META_TYPE.IMAGE,
        },
        image_m: {
          type: META_TYPE.IMAGE,
        },
        image_s: {
          type: META_TYPE.IMAGE,
        },
        inputPlaceholderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        placeholder: {
          type: META_TYPE.STRING,
        },
        searchClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        storeSelectClickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        title: {
          type: META_TYPE.STRING,
        },
        titleTextColor: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    storeMenu: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/StoreMenuHeader')),
      defaults: {
        sorting: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_hover: {
          type: META_TYPE.IMAGE,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    terms: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Terms')),
      defaults: {
        text: '',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchorLink: {
          type: META_TYPE.INTEGER,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    text: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Text')),
      defaults: {
        headerFooterPadding: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        body: {
          type: META_TYPE.TEXT,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    headerTitle: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/HeaderTitle')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    textV2: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/TextV2')),
      defaults: {
        headerFooterPadding: false,
        leftIcon: undefined,
        rightIcon: undefined,
        loopAnimation: false,
      },
      settings: {},
      meta: {},
    },
    textConfigure: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/TextV2')),
      defaults: {
        headerFooterPadding: false,
        leftIcon: undefined,
        rightIcon: undefined,
        loopAnimation: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        body: {
          type: META_TYPE.TEXT,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    textWithIcon: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/TextV2')),
      defaults: {
        headerFooterPadding: false,
        leftIcon: undefined,
        rightIcon: undefined,
        loopAnimation: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        body: {
          type: META_TYPE.TEXT,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    hover: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Hover')),
      defaults: {
        disableAnimation: false,
        footerPadding: false,
        imageSrc: '',
        maxTextLength: 65,
        minHeight: 'auto',
        subtitle: '',
        text: '',
        title: '',
        titleVisible: false,
        titleHeight: '2',
        fullSize: false,
        mobileInfoButtonIcon: DEFAULT_MOBILE_INFO_BUTTON_ICON,
      },
      defaultMetaData: {
        asyncImage: false,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        anchor: {
          type: META_TYPE.INTEGER,
        },
        disableAnimation: {
          type: META_TYPE.BOOLEAN,
        },
        fullsize: {
          type: META_TYPE.BOOLEAN,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        image_l_focus: enumConfigs.focus,
        image_m: {
          type: META_TYPE.IMAGE,
        },
        image_m_focus: enumConfigs.focus,
        image_s: {
          type: META_TYPE.IMAGE,
        },
        image_billboard: {
          type: META_TYPE.IMAGE,
        },
        image_s_focus: enumConfigs.focus,
        min_height: {
          type: META_TYPE.STRING,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        subtitle: {
          type: META_TYPE.TEXT,
        },
        text: {
          type: META_TYPE.TEXT,
        },
        text_length: {
          type: META_TYPE.INTEGER,
        },
        title: {
          type: META_TYPE.TEXT,
        },
        title_globalSettingEnabled: {
          type: META_TYPE.BOOLEAN,
        },
        title_height: {
          type: META_TYPE.STRING,
        },
        title_visible: {
          type: META_TYPE.BOOLEAN,
        },
        mobileLayer: {
          type: META_TYPE.BOOLEAN,
        },
        image_dynamicScaling: {
          type: META_TYPE.BOOLEAN,
        },
        asyncImage: {
          type: META_TYPE.BOOLEAN,
        },
        loadImageOnHover: {
          type: META_TYPE.BOOLEAN,
        },
        image_enableSizeSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        image_size: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        image_size_s: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        image_size_m: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        image_size_b: {
          type: META_TYPE.OBJECT,
          structure: {
            width: { type: META_TYPE.INTEGER },
            height: { type: META_TYPE.INTEGER },
          },
        },
        image_margin: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        image_margin_s: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        image_margin_m: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
        image_margin_b: {
          type: META_TYPE.OBJECT,
          structure: {
            top: { type: META_TYPE.INTEGER },
            right: { type: META_TYPE.INTEGER },
            bottom: { type: META_TYPE.INTEGER },
            left: { type: META_TYPE.INTEGER },
          },
        },
      },
    },
    vastVideoLight: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/VastVideo/VastVideoLight')),
      defaults: {
        showPlayButton: true,
        start: 0,
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        duration: {
          type: META_TYPE.INTEGER,
        },
        link: {
          type: 'url',
        },
        source: {
          type: 'url',
        },
        start: {
          type: META_TYPE.RANGE,
        },
        url: {
          type: 'url',
          required: true,
        },
        url_m: {
          type: 'url',
        },
        url_s: {
          type: 'url',
        },
        url_b: {
          type: 'url',
        },
        enableDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    video: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/YouTubeVideo')),
      defaults: {},
      settings: {},
      meta: {
        autoplay: {
          type: META_TYPE.BOOLEAN,
        },
        controls: {
          type: META_TYPE.BOOLEAN,
        },
        loop: {
          type: META_TYPE.BOOLEAN,
        },
        playOnMouseOver: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
        startPlayPayout: {
          type: META_TYPE.BOOLEAN,
        },
        unmute: {
          type: META_TYPE.BOOLEAN,
        },
        url: {
          type: META_TYPE.STRING,
        },
        urlHPA: {
          type: META_TYPE.STRING,
        },
        videoPlay100Percent: {
          type: META_TYPE.BOOLEAN,
        },
        videoPlay100PercentPayout: {
          type: META_TYPE.BOOLEAN,
        },
        ytModestBranding: {
          type: META_TYPE.BOOLEAN,
        },
        ytOptionFs: {
          type: META_TYPE.BOOLEAN,
        },
        ytOptionMute: {
          type: META_TYPE.BOOLEAN,
        },
        ytRel: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    vastVideoXL: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/VastVideo/VastVideoXL')),
      defaults: {
        showCta: false,
        autoplay: true,
        controls: true,
        controlsColor: '#fff',
        higherPositionedControlBar: false,
        onDemand: false,
        start: 0,
        url: '',
        showPlayButton: true,
      },
      settings: {},
      meta: {
        autoplay: {
          type: META_TYPE.BOOLEAN,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        controls: {
          type: META_TYPE.BOOLEAN,
        },
        controlsColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        duration: {
          type: META_TYPE.INTEGER,
        },
        enableDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        internalReference: {
          type: META_TYPE.INTEGER,
        },
        link: {
          type: 'url',
        },
        onDemand: {
          type: META_TYPE.BOOLEAN,
        },
        source: {
          type: 'url',
        },
        start: {
          type: META_TYPE.RANGE,
        },
        url: {
          type: 'url',
          required: true,
        },
        url_m: {
          type: 'url',
        },
        url_s: {
          type: 'url',
        },
      },
    },
    viewLater: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ViewLater')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        inputPayout: {
          type: META_TYPE.BOOLEAN,
        },
        socialmedia_link: {
          type: META_TYPE.BOOLEAN,
        },
        label_header: {
          type: META_TYPE.TEXT,
        },
        label_paragraph: {
          type: META_TYPE.TEXT,
        },
        label_sub: {
          type: META_TYPE.TEXT,
        },
        submitPayout: {
          type: META_TYPE.BOOLEAN,
        },
        text: {
          type: META_TYPE.STRING,
        },
      },
    },
    logo: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Logo')),
      defaults: {
        hoverText: 'Logo',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        image_l: {
          type: META_TYPE.IMAGE,
          required: true,
        },
        individualLogo: {
          type: META_TYPE.BOOLEAN,
        },
        logo: {
          type: META_TYPE.STRING,
        },
      },
    },
    likeButton: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/LikeButton')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.payouts,
        color: {
          type: META_TYPE.COLOR_PICKER,
          required: true,
        },
        hoverColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        initialCount: {
          type: META_TYPE.INTEGER,
          required: true,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
      },
    },
    heading: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Heading')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        body: {
          type: META_TYPE.TEXT,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        productRelation: {
          type: META_TYPE.PRODUCT_ID,
        },
      },
    },
    sharingButton: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SharingButton')),
      defaults: {},
      settings: {},
      meta: {
        color: {
          type: META_TYPE.COLOR_PICKER,
          required: true,
        },
        initialCount: {
          type: META_TYPE.INTEGER,
          required: true,
        },
        textColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    sharingButtonFooter: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/SharingButtonFooter')),
      defaults: {},
      settings: {},
      meta: {
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        color: {
          type: META_TYPE.COLOR_PICKER,
          required: true,
        },
        initialCount: {
          type: META_TYPE.INTEGER,
          required: true,
        },
      },
    },
    tag: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/Tag')),
      defaults: {},
      settings: {},
      meta: {
        text: {
          type: META_TYPE.TEXT,
          required: true,
        },
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
          required: true,
        },
        borderRadius: {
          type: META_TYPE.INTEGER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    videoControls: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/VideoControls')),
      defaults: {},
      settings: {},
      meta: {
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        color: {
          type: META_TYPE.COLOR_PICKER,
          required: true,
        },
        muteButton: {
          type: META_TYPE.BOOLEAN,
        },
        pauseButton: {
          type: META_TYPE.BOOLEAN,
        },
      },
    },
    productSwiper: {
      wrapper: null,
      defaults: {},
      settings: {},
      meta: {},
    },
    productCard: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ProductCard')),
      defaults: {
        productsPerRow: {
          l: 3,
          m: 2,
          s: 2,
        },
        enableProductsPerRowSettingsDifferentViewports: true,
        backgroundColor: '#fff',
        categoryBackgroundColor: '#e3e3e3',
        categoryBorderColor: '#cdcdcd',
        categoryTextColor: defaultTheme.colors.backgroundColorDark,
        categoryActiveBackgroundColor: defaultTheme.colors.backgroundColorDark,
        categoryActiveBorderColor: defaultTheme.colors.backgroundColorDark,
        categoryActiveTextColor: '#fff',
        pricePatchTextColor: '#fff',
        pricePatchBackgroundColor: 'rgba(0, 0, 0, 0.7)',
        pricePatchBorderColor: 'rgba(0, 0, 0, 0.7)',
      },
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        backgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categories: {
          type: META_TYPE.JSON,
          structure: {
            '*': { type: META_TYPE.STRING },
          },
          normalize: (value) => ArrayToObject(value as objectType | string[]),
        },
        enableProductsPerRowSettingsDifferentViewports: {
          type: META_TYPE.BOOLEAN,
        },
        pricePatchBorderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryActiveBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryActiveBorderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryActiveTextColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryBorderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        categoryTextColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        clickPayout: {
          type: META_TYPE.BOOLEAN,
        },
        enableExpand: {
          type: META_TYPE.BOOLEAN,
        },
        pricePatchBackgroundColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        pricePatchTextColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        productsPerRow: {
          type: META_TYPE.OBJECT,
          structure: {
            l: { type: META_TYPE.INTEGER },
            m: { type: META_TYPE.INTEGER },
            s: { type: META_TYPE.INTEGER },
          },
        },
        title: {
          type: META_TYPE.STRING,
        },
      },
    },
    productCardProduct: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ProductCardProduct')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        ...metaConfigs.payouts,
        endCardProduct_description: {
          type: META_TYPE.STRING,
        },
        endCardProduct_image: {
          type: META_TYPE.IMAGE,
        },
        endCardProduct_price: {
          type: META_TYPE.STRING,
        },
        buttonText: {
          type: META_TYPE.STRING,
        },
        category: {
          type: META_TYPE.STRING,
        },
        description: {
          type: META_TYPE.STRING,
        },
        image: {
          type: META_TYPE.IMAGE,
        },
        link: {
          type: META_TYPE.STRING,
        },
        price: {
          type: META_TYPE.STRING,
        },
      },
    },
    expandableProductCard: {
      wrapper: lazy(() => import('@ad-lucy/components/builderComponents/ExpandableProductCard')),
      defaults: {},
      settings: {},
      meta: {
        ...metaConfigs.navigation,
        animationBorderColor: {
          type: META_TYPE.COLOR_PICKER,
        },
        description: {
          type: META_TYPE.STRING,
        },
        pricePatch: {
          type: META_TYPE.OBJECT,
          structure: {},
        },
        slider: {
          type: META_TYPE.OBJECT,
          structure: {},
        },
        stickyCTA: {
          type: META_TYPE.OBJECT,
          structure: {},
        },
        title: {
          type: META_TYPE.STRING,
        },
        page: {
          type: META_TYPE.STRING,
        },
      },
    },
  },
} as const satisfies FullCreativesConfig;

type DefaultCreativesConfig = typeof defaultCreativesConfig;

export { defaultCreativesConfig };
export type { DefaultCreativesConfig };
